(ns practitest.frontend.project-general-settings.views
  (:require
    [clojure.string :as str]
    [re-frame.core :as re-frame]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.container-card :as container-card]
    [practitest.frontend.components2.info-note :as info-note]
    [practitest.frontend.components2.tooltip :as tooltip]
    [practitest.frontend.integration-settings.shared-form :as shared-form]
    [practitest.frontend.project-general-settings.events :as events]))

(def ^:private name-max-length 30)
(def ^:private description-max-length 100)

(def ^:private api-help-url "https://www.practitest.com/api-v2")
(def ^:private support-email "support@practitest.com")

(defn- corporate-tag []
  [:span.badge.bg-light.text-primary
   (get-translation "project-settings.general.corporate-only" "Corporate only")])

(defn- field-errors [errors]
  (when (seq errors)
    [:div.validation-error-message (str/join ", " errors)]))

(defn- logo-field [{:keys [name logo_url]} {:keys [logo-preview]}]
  (let [src        (or logo-preview logo_url)
        file-input (atom nil)]
    [:div.general-settings-field.flex-shrink-0
     [:label.m-label (get-translation "project-settings.general.logo" "Logo")]
     [:div.d-flex.align-items-center.gap-2
      (if src
        [:img.general-settings-logo-tile {:src src :alt ""}]
        [:span.general-settings-logo-tile (fu/user-initials name)])
      [:input.d-none
       {:type      "file"
        :accept    ".jpg,.jpeg,.png,.gif"
        :ref       #(reset! file-input %)
        :on-change (fn [e]
                     (when-let [file (-> e .-target .-files (aget 0))]
                       (re-frame/dispatch [::events/choose-logo file]))
                     (set! (.. e -target -value) ""))}]
      [buttons/button {:test-id  "general-settings-upload-logo"
                       :text     (get-translation "project-settings.general.upload-logo" "Upload Logo")
                       :on-click #(some-> @file-input .click)}]
      (when logo_url
        [buttons/button {:tertiary? true
                         :test-id   "general-settings-remove-logo"
                         :text      (get-translation "generic.button.remove" "Remove")
                         :on-click  #(re-frame/dispatch [::events/set-remove-logo-confirm true])}])]
     [:div.general-settings-hint
      (get-translation "project-settings.general.logo-hint" "JPG, PNG, GIF | 1 MB")]]))

(defn project-details [data form errors]
  [container-card/container-card
   {:title (get-translation "project-settings.general.project-details" "Project Details")
    :class "general-settings-card general-settings-details"}
   [:div.d-flex.gap-3
    [:div.general-settings-field.general-settings-field-name
     [:label.m-label {:for "general-settings-name"}
      (get-translation "project-settings.general.project-name" "Project Name")]
     [:input.m-input
      (dom/add-test-id
        {:id         "general-settings-name"
         :class      (when (seq (:name errors)) "errors")
         :max-length name-max-length
         :value      (:name form)
         :on-change  #(re-frame/dispatch [::events/set-field :name (dom/event-value %)])}
        "general-settings-name")]
     [field-errors (:name errors)]]
    [:div.general-settings-field.general-settings-field-id
     [:label.m-label {:for "general-settings-id"}
      (get-translation "project-settings.general.project-id" "Project ID")]
     [:input.m-input.disabled {:id "general-settings-id" :value (:id data) :disabled true}]
     [:div.general-settings-hint
      (get-translation "project-settings.general.project-id-hint" "Use this ID in API and MCP calls.")
      " "
      [:a {:href api-help-url :target "_blank" :rel "noopener noreferrer"}
       (get-translation "users-view.new-user.learn-how" "Learn how")]]]
    [logo-field data form]]
   [:div.general-settings-field
    [:label.m-label {:for "general-settings-description"}
     (get-translation "settings.edit-form.section.description" "Description")]
    [:textarea.m-input.general-settings-description
     (dom/add-test-id
       {:id          "general-settings-description"
        :class       (when (seq (:description errors)) "errors")
        :max-length  description-max-length
        :placeholder (format-translation (get-translation "project-settings.general.description-placeholder"
                                                          "Optional description for %{name}")
                                         {:name (:name data)})
        :value       (:description form)
        :on-change   #(re-frame/dispatch [::events/set-field :description (dom/event-value %)])}
       "general-settings-description")]
    [field-errors (:description errors)]]])

(defn- setting-row [{:keys [test-id title description corporate? checked? disabled? disabled-tooltip on-change]}]
  [:div.general-settings-setting
   (dom/add-test-id {} test-id)
   [:div
    [:div.d-flex.align-items-center.gap-2 title (when corporate? [corporate-tag])]
    [:div.general-settings-setting-description description]]
   (let [control (fn [props]
                   [:label.m-checkbox-label props
                    [shared-form/checkbox
                     {:checked?  (boolean checked?)
                      :disabled? (boolean disabled?)
                      :on-change #(on-change (.. % -target -checked))}]
                    (get-translation "generic.label.enabled" "Enabled")])]
     (if (and disabled? disabled-tooltip)
       [tooltip/tooltip control disabled-tooltip]
       [control {}]))])

(defn- locked? [data setting]
  (some #{(name setting)} (:locked_settings data)))

(defn- not-in-plan-tooltip []
  (get-translation "project-settings.general.not-in-plan" "This feature isn't included in your plan"))

(defn- settings-card [title subtitle rows data form & extra-rows]
  (-> [container-card/container-card
       {:title         title
        :header-action (when subtitle [:span.general-settings-hint subtitle])
        :class         "general-settings-card"}]
      (into (for [{:keys [setting permission-tooltip] :as row} rows]
              ^{:key setting}
              [setting-row
               (assoc row
                      :test-id          (str "general-settings-" (name setting))
                      :checked?         (get-in form [:settings setting])
                      :disabled?        (locked? data setting)
                      :disabled-tooltip (if (and permission-tooltip (:enforce_run_order_permission_locked data))
                                          permission-tooltip
                                          (not-in-plan-tooltip))
                      :on-change        #(re-frame/dispatch [::events/set-setting setting %]))]))
      (into extra-rows)))

(defn modules-and-features [data form]
  [settings-card
   (get-translation "project-settings.general.modules-and-features" "Modules & Features")
   (get-translation "project-settings.general.modules-and-features-hint"
                    "Turn features on or off for everyone in this project")
   [{:setting     :task_board_support
     :title       (get-translation "navigation.project-menu.taskboard" "Task Board")
     :description (get-translation "project-settings.general.task-board-description"
                                   "A board view of test sets, instances and issues, so the team can track work by status.")}
    {:setting     :time_management_support
     :title       (get-translation "project-settings.general.time-management" "Time Management")
     :description (get-translation "project-settings.general.time-management-description"
                                   "Track estimated and actual duration on tests, test sets and runs.")}
    {:setting     :value_score
     :corporate?  true
     :title       (get-translation "value-score.title" "Value Score")
     :description (get-translation "project-settings.general.value-score-description"
                                   "Rank tests by the value they add, to help decide what to run first.")}
    {:setting     :status_flow_enabled
     :corporate?  true
     :title       (get-translation "project-settings.general.test-workflow" "Test Workflow")
     :description (get-translation "project-settings.general.test-workflow-description"
                                   "Move tests through review and approval statuses before they can be run.")}
    {:setting     :test_case_versioning_enabled
     :corporate?  true
     :title       (get-translation "test.versioning.title" "Test Versioning")
     :description (get-translation "project-settings.general.test-versioning-description"
                                   "Keep a version history for each test and choose which version a test set runs.")}]
   data form])

(defn- annotation-types-row []
  [:div.general-settings-setting
   (dom/add-test-id {} "general-settings-annotation_types")
   [:div
    [:div (get-translation "project-settings.general.annotation-types" "Annotation Types")]
    [:div.general-settings-setting-description
     (get-translation "project-settings.general.annotation-types-description"
                      "Annotations during an ET Run - you can configure types and color indicators.")]]
   [buttons/button {:test-id  "general-settings-customize-annotations"
                    :text     (get-translation "landing.customize" "Customize")
                    :on-click #(re-frame/dispatch [::events/open-annotation-modal])}]])

(defn test-runs [data form]
  [settings-card
   (get-translation "project-settings.general.test-runs" "Test Runs")
   nil
   (cond-> [{:setting            :enforce_run_order
             :title              (get-translation "testset.instances.enforce-run-order" "Enforce Run Order")
             :description        (get-translation "project-settings.general.enforce-run-order-description"
                                                  "This sets the default, override per test set if needed")
             :permission-tooltip (get-translation "project.settings.enforce_run_order_permissions"
                                                  "Only administrators or users with 'Project, Users & Permissions, Integrations' permission can modify this setting")}
            {:setting     :notify_next_instance_assignee
             :title       (get-translation "project-settings.general.notify-next-tester" "Notify Next Tester")
             :description (get-translation "project-settings.general.notify-next-tester-description"
                                           "Enabling this checkbox will notify users when it's their turn to run an instance. This feature is not available in the fast run option. Users can disable notifications in their personal settings")}
            {:setting     :show_fast_run
             :title       (get-translation "project-settings.general.fast-run" "Fast Run")
             :description (get-translation "project-settings.general.fast-run-description"
                                           "Set a status for a whole test instance at once, without opening each step.")}]
     (:enforce_test_traceability_visible data)
     (conj {:setting     :enforce_test_traceability
            :title       (get-translation "project.settings.enforce_test_traceability" "Enforce Test Traceability")
            :description (get-translation "project-settings.general.enforce-test-traceability-description"
                                          "Tests must be linked to at least one requirement before they can be saved.")}))
   data form
   [annotation-types-row]])

(defn- automation-rows []
  [{:type        "api"
    :title       (get-translation "project-settings.general.api-tests" "API Tests")
    :description (get-translation "project-settings.general.api-tests-description" "Allow creating API test type in the Test Library.")
    :help-url    "https://www.practitest.com/api-v2"}
   {:type        "firecracker"
    :title       (get-translation "project-settings.general.firecracker-tests" "FireCracker Tests")
    :description (get-translation "project-settings.general.firecracker-tests-description" "Allow creating FireCracker test type in the Test Library.")
    :help-url    "https://www.practitest.com/help/methodology-tips/firecracker/"}
   {:type        "xbot"
    :title       (get-translation "project-settings.general.xbot-tests" "xBot Tests")
    :description (get-translation "project-settings.general.xbot-tests-description" "Allow creating xBot test type in the Test Library.")
    :help-url    "https://www.practitest.com/help/automation/xbot/"}])

(defn import-api-automation [data form]
  (into
    [settings-card
     (get-translation "project-settings.general.import-api-automation" "Import, API & Automation")
     (get-translation "project-settings.general.import-api-automation-hint"
                      "Automated results are always accepted into PractiTest")
     [{:setting     :add_values_on_import_api
       :title       (get-translation "project-settings.general.add-list-values" "Add New List Values on Import / API")
       :description (get-translation "project-settings.general.add-list-values-description"
                                     "This will allow adding new list values during import or API updates. If disabled, unrecognized values will cause an error. Global fields are not included and can not be updated in any case.")}]
     data form]
    (for [{:keys [type title description help-url]} (automation-rows)]
      ^{:key type}
      [setting-row
       {:test-id          (str "general-settings-automation-" type)
        :title            title
        :description      [:<>
                           description
                           " "
                           [:a {:href help-url :target "_blank" :rel "noopener noreferrer"}
                            (get-translation "users-view.new-user.learn-how" "Learn how")]]
        :checked?         (contains? (:automation_types form) type)
        :disabled?        (:automation_locked data)
        :disabled-tooltip (not-in-plan-tooltip)
        :on-change        #(re-frame/dispatch [::events/set-automation type %])}])))

(def ^:private owners-shown 3)

(defn- owner-links [owners]
  (interpose ", " (for [{:keys [name email]} owners]
                    [:a {:href (str "mailto:" email)} name])))

(defn- account-owners [owners]
  (if (> (count owners) owners-shown)
    [tooltip/tooltip
     (fn [props]
       (-> [:div props]
           (into (owner-links (take owners-shown owners)))
           (conj "...")))
     (into [:div] (for [{:keys [name]} owners] [:div name]))]
    (into [:div] (owner-links owners))))

(defn project-info [{:keys [info]}]
  (let [{:keys [created expires account_owners]} info]
    [container-card/container-card
     {:title (get-translation "navigation.project-menu.project-info" "Project Info")
      :class "general-settings-card general-settings-info"}
     [:div
      [:div.general-settings-hint (get-translation "generic.label.created" "Created")]
      [:div created]]
     (when expires
       [:div
        [:div.general-settings-hint
         (get-translation "project-settings.general.subscription-expires" "Subscription Expires")]
        [:div expires]])
     [:div
      [:div.general-settings-hint (get-translation "account.owner.label" "Account Owner")]
      (if (seq account_owners)
        [account-owners account_owners]
        [:div.general-settings-info-empty
         (get-translation "project-settings.general.no-account-owner" "No account owner")])]
     [info-note/mux-info-note
      {:class   "general-settings-info-note"
       :message [:<>
                 (get-translation "project-settings.general.renew" "To renew the project subscription,")
                 " "
                 [:a (dom/add-test-id
                       {:href     (str "mailto:" support-email)
                        :on-click #(re-frame/dispatch [::events/contact-support-clicked])}
                       "general-settings-contact-support")
                  (get-translation "project-settings.general.contact-support" "contact support")]
                 "."]}]]))
